/**
 * Fade carousel block — Swiper crossfade with synced caption overlay.
 */

const initializedCarousels = new WeakSet();
const pendingCarousels = [];

class FadeCarousel {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.captionEl = element.querySelector('.fade-carousel__caption');
    this.footerEl = element.querySelector('[data-fade-carousel-footer]');
    this.transitionSpeed = parseInt(element.dataset.transitionSpeed, 10) || 1200;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (initializedCarousels.has(element)) return;
    initializedCarousels.add(element);

    element._fadeCarouselInstance = this;
    this.setInitialCaption();
    this.setInitialLink();
    this.init();
  }

  getSlide(index) {
    const domSlides = this.element.querySelectorAll('.fade-carousel__slide');
    return domSlides[index] || this.swiper?.slides[index] || null;
  }

  getSlideCaption(index) {
    return this.getSlide(index)?.dataset.slideCaption || '';
  }

  getSlideLink(index) {
    return this.getSlide(index)?.dataset.slideLink || '';
  }

  setInitialCaption() {
    if (!this.captionEl) return;

    const caption = this.getSlideCaption(0);
    if (!caption) return;

    this.captionEl.classList.remove('is-transitioning');
    this.captionEl.textContent = caption;
  }

  setInitialLink() {
    this.updateFooterLink(0);
  }

  updateFooterLink(index) {
    if (!this.footerEl) return;

    const link = this.getSlideLink(index);
    if (link) {
      this.footerEl.setAttribute('href', link);
    } else {
      this.footerEl.removeAttribute('href');
    }
  }

  getOptions() {
    const autoplay = this.element.dataset.autoplay === 'true';
    const loop = this.element.dataset.loop === 'true';
    const delay = parseInt(this.element.dataset.autoplayDelay, 10) || 4000;
    const speed = this.transitionSpeed;
    const slideCount = this.element.querySelectorAll('.fade-carousel__slide').length;
    const reducedMotion = this.reducedMotionQuery.matches;

    const options = {
      slidesPerView: 1,
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      speed,
      loop: false,
      rewind: loop && slideCount > 1 && !reducedMotion,
      watchSlidesProgress: true,
      allowTouchMove: slideCount > 1,
    };

    if (autoplay && slideCount > 1 && !reducedMotion) {
      options.autoplay = {
        delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
        waitForTransition: true,
      };
    }

    return options;
  }

  updateCaption(index, animate = true) {
    if (!this.captionEl) return;

    const caption = this.getSlideCaption(index);
    if (!caption) return;

    if (this.captionEl.textContent === caption) return;

    if (!animate || this.reducedMotionQuery.matches) {
      this.captionEl.classList.remove('is-transitioning');
      this.captionEl.textContent = caption;
      return;
    }

    this.captionEl.classList.add('is-transitioning');

    window.setTimeout(() => {
      this.captionEl.textContent = caption;
      this.captionEl.classList.remove('is-transitioning');
    }, Math.round(this.transitionSpeed / 2));
  }

  init() {
    if (this.swiper) return;

    const Swiper = window.Swiper;
    const swiperEl = this.element.querySelector('.fade-carousel__media');

    if (!Swiper) {
      if (!pendingCarousels.includes(this)) {
        pendingCarousels.push(this);
      }
      return;
    }

    if (!swiperEl) return;

    const slideCount = this.element.querySelectorAll('.fade-carousel__slide').length;
    if (slideCount === 0) return;

    this.setInitialCaption();

    this.swiper = new Swiper(swiperEl, {
      ...this.getOptions(),
      on: {
        init: (swiper) => {
          this.element.classList.add('swiper-initialized');
          this.loadActiveSlideMedia(swiper);
          this.updateCaption(swiper.activeIndex, false);
          this.updateFooterLink(swiper.activeIndex);
        },
        slideChangeTransitionStart: (swiper) => {
          this.updateCaption(swiper.activeIndex);
          this.updateFooterLink(swiper.activeIndex);
        },
        slideChangeTransitionEnd: (swiper) => {
          this.loadActiveSlideMedia(swiper);
        },
      },
    });
  }

  loadActiveSlideMedia(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    if (!slide) return;

    slide.querySelectorAll('[data-lazy-image]:not(.loaded)').forEach((img) => {
      const src = img.dataset.src;
      const srcset = img.dataset.srcset;

      if (!src) return;

      if (srcset) img.srcset = srcset;
      img.src = src;
      img.classList.add('loaded');
      img.style.opacity = '1';
    });

    if (window.LazyImageLoader) {
      window.__lazyImageLoaderActive = false;
      new window.LazyImageLoader();
    }
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.element.classList.remove('swiper-initialized');
  }
}

function initFadeCarousels(root = document) {
  root.querySelectorAll('[data-fade-carousel]').forEach((element) => {
    if (element.dataset.fadeCarouselInit === 'true') return;
    element.dataset.fadeCarouselInit = 'true';
    new FadeCarousel(element);
  });
}

function flushPendingCarousels() {
  if (typeof window.Swiper === 'undefined') return;

  while (pendingCarousels.length) {
    const instance = pendingCarousels.shift();
    instance.init();
  }
}

function watchSwiper() {
  if (typeof window.Swiper !== 'undefined') {
    flushPendingCarousels();
    return;
  }

  window.setTimeout(watchSwiper, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initFadeCarousels();
    watchSwiper();
  });
} else {
  initFadeCarousels();
  watchSwiper();
}

document.addEventListener('shopify:section:load', (event) => {
  window.setTimeout(() => {
    initFadeCarousels(event.target);
    flushPendingCarousels();
  }, 100);
});

document.addEventListener('shopify:block:select', (event) => {
  const carousel = event.target.closest('[data-fade-carousel]');
  if (!carousel) return;

  window.setTimeout(() => {
    if (carousel._fadeCarouselInstance) {
      carousel._fadeCarouselInstance.destroy();
    }

    carousel.dataset.fadeCarouselInit = '';
    initializedCarousels.delete(carousel);
    initFadeCarousels(carousel.parentElement || document);
    flushPendingCarousels();
  }, 100);
});

window.FadeCarousel = FadeCarousel;
